import { useEffect, useState } from 'react';
import { Sparkles, Loader2, CheckCircle2, AlertTriangle } from 'lucide-react';
import clsx from 'clsx';
import { aiImageApi, type ImageModelDTO, type AiImageResult } from '@/api/aiImage';
import { draftsApi } from '@/api/storyDrafts';
import { walletApi } from '@/api/wallet';
import { useAuthStore } from '@/store/authStore';

const SUBUNIT = 100_000; // 1 SWIPA = 100 000 sub-units

interface Props {
  /** Вызывается после публикации/сохранения — обновить ленту/закрыть. */
  onPublished: () => void;
}

const ASPECTS: Array<{ value: '9:16' | '1:1' | '16:9'; label: string }> = [
  { value: '9:16', label: 'Вертикальное 9:16' },
  { value: '1:1', label: 'Квадрат 1:1' },
  { value: '16:9', label: 'Широкое 16:9' },
];

export default function AiImagePanel({ onPublished }: Props) {
  const [models, setModels] = useState<ImageModelDTO[]>([]);
  // Без дефолта: модель выбирает пользователь явно (осознанная трата SWIPA).
  const [modelId, setModelId] = useState<string>('');
  const [aspect, setAspect] = useState<'9:16' | '1:1' | '16:9'>('9:16');
  const [prompt, setPrompt] = useState('');
  const [busy, setBusy] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [result, setResult] = useState<AiImageResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const m = await aiImageApi.models();
        if (!alive) return;
        setModels(m);
        // Модель НЕ выбираем автоматически — это осознанный выбор пользователя.
      } catch {
        if (alive) setError('Не удалось загрузить движки');
      }
    })();
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const model = models.find((m) => m.id === modelId);
  const price = model?.priceSwipa ?? null;

  const generate = async () => {
    if (!modelId) {
      setError('Выберите модель — вы платите за конкретную модель');
      return;
    }
    if (prompt.trim().length < 3) {
      setError('Опиши картинку — хотя бы пару слов');
      return;
    }
    if (!price) {
      setError('Не удалось рассчитать цену');
      return;
    }
    setBusy(true);
    setError(null);
    let paidRef: string | null = null;
    try {
      const userId = useAuthStore.getState().user?.id;
      if (!userId) throw new Error('Нужно войти');
      // Подпись пользователя за конкретную модель → оплата → только потом генерация.
      await walletApi.enroll(userId);
      const jobId = crypto.randomUUID();
      paidRef = `gen:${jobId}`;
      const pay = await walletApi.debitSigned(userId, {
        amountSub: String(price * SUBUNIT),
        purpose: 'generation:main-ai-image',
        ref: paidRef,
        memo: `ai-image:${modelId}`,
      });
      const res = await aiImageApi.generate({
        prompt: prompt.trim(),
        modelId,
        aspectRatio: aspect,
        jobId,
        txHash: pay.txHash,
      });
      setResult(res);
    } catch (err: any) {
      if (paidRef) await walletApi.cancelUnclaimed(paidRef).catch(() => undefined);
      const msg = err?.response?.data?.message;
      setError(
        msg === 'INSUFFICIENT_FUNDS'
          ? `Недостаточно SWIPA (нужно ${price})`
          : (msg ?? err?.message ?? 'Не удалось сгенерировать картинку'),
      );
    } finally {
      setBusy(false);
    }
  };

  const publishDraft = async () => {
    if (!result?.draftId) return;
    setPublishing(true);
    try {
      await draftsApi.publish(result.draftId);
      onPublished();
    } catch (err: any) {
      setError(err?.response?.data?.message ?? 'Не удалось опубликовать');
    } finally {
      setPublishing(false);
    }
  };

  const reset = () => {
    setResult(null);
    setError(null);
  };

  // ── Состояние: генерация ────────────────────────────────────────────────────
  if (busy) {
    return (
      <div className="flex flex-col items-center justify-center gap-3 py-10 text-center">
        <Loader2 size={32} className="animate-spin text-brand-400" />
        <p className="text-sm text-surface-200">Генерация картинки…</p>
        <p className="text-xs text-surface-500 max-w-xs">Обычно 5–30 секунд.</p>
      </div>
    );
  }

  // ── Состояние: готово ───────────────────────────────────────────────────────
  if (result) {
    return (
      <div className="flex flex-col items-center gap-4 py-6 text-center">
        <CheckCircle2 size={32} className="text-emerald-400" />
        <div>
          <p className="text-sm text-surface-100 font-medium">Картинка готова и сохранена в черновики</p>
          <p className="text-xs text-surface-500 mt-1">Можно опубликовать сразу или отредактировать позже.</p>
        </div>
        {error && <p className="text-red-400 text-xs">{error}</p>}
        <div className="flex gap-2 w-full">
          <button
            onClick={publishDraft}
            disabled={publishing || !result.draftId}
            className="flex-1 py-2 bg-brand-600 hover:bg-brand-500 disabled:opacity-40 rounded-lg text-white text-sm font-medium"
          >
            {publishing ? 'Публикуем…' : 'Опубликовать'}
          </button>
          <button onClick={onPublished} className="px-3 py-2 bg-surface-800 hover:bg-surface-700 rounded-lg text-surface-200 text-sm">
            В черновики
          </button>
        </div>
        <button onClick={reset} className="text-xs text-surface-400 hover:text-surface-200">
          Сгенерировать ещё
        </button>
      </div>
    );
  }

  // ── Состояние: форма ────────────────────────────────────────────────────────
  return (
    <div className="space-y-4">
      <textarea
        value={prompt}
        onChange={(e) => setPrompt(e.target.value)}
        placeholder="Опиши картинку: «котёнок-космонавт в шлеме, неоновый фон, постер»"
        maxLength={1000}
        rows={3}
        className="w-full bg-surface-800 border border-surface-700 rounded-lg px-3 py-2 text-sm text-surface-100 placeholder-surface-500 outline-none focus:border-brand-500 resize-none"
      />

      {/* Соотношение сторон */}
      <div className="flex gap-2">
        {ASPECTS.map((a) => (
          <button
            key={a.value}
            onClick={() => setAspect(a.value)}
            className={clsx(
              'flex-1 px-2 py-1.5 rounded-lg text-xs transition-colors',
              aspect === a.value ? 'bg-brand-600 text-white' : 'bg-surface-800 text-surface-400 hover:bg-surface-700',
            )}
          >
            {a.label}
          </button>
        ))}
      </div>

      {/* Выбор движка */}
      <div>
        <p className="text-xs text-surface-400 mb-2">Движок</p>
        <div className="grid grid-cols-3 gap-2">
          {models.map((m) => (
            <button
              key={m.id}
              onClick={() => setModelId(m.id)}
              className={clsx(
                'text-left p-2.5 rounded-lg border transition-colors',
                modelId === m.id ? 'border-brand-500 bg-brand-600/15' : 'border-surface-700 bg-surface-800 hover:border-surface-600',
              )}
            >
              <div className="flex items-center justify-between gap-1">
                <span className="text-xs font-medium text-surface-100">{m.label}</span>
              </div>
              <p className="text-[10px] text-surface-500 mt-0.5 leading-tight">{m.hint}</p>
              <p className="text-[10px] text-brand-300 mt-1">{m.priceSwipa} ₷</p>
            </button>
          ))}
        </div>
      </div>

      {error && <p className="text-red-400 text-xs">{error}</p>}

      <div className="flex items-center justify-between text-xs text-surface-400">
        <span>
          Стоимость: <span className="text-surface-100 font-medium">{price ?? '—'} SWIPA</span>
          {model && <span className="text-surface-500"> · {model.label}</span>}
        </span>
      </div>

      <button
        onClick={generate}
        disabled={busy || prompt.trim().length < 3 || !modelId}
        className="w-full flex items-center justify-center gap-2 py-2.5 bg-brand-600 hover:bg-brand-500 disabled:opacity-40 rounded-lg text-white text-sm font-medium transition-colors"
      >
        <Sparkles size={16} />
        {!modelId ? 'Выберите модель' : `Сгенерировать${price ? ` · ${price} SWIPA` : ''}`}
      </button>

      {!models.length && !error && (
        <div className="flex items-center justify-center gap-2 text-xs text-surface-500 py-1">
          <AlertTriangle size={12} /> Загрузка движков…
        </div>
      )}
    </div>
  );
}
